import { SkillCategory } from '../../types/skillProgress';

export const badmintonSkillCategories: SkillCategory[] = [
  {
    id: 'serving',
    name: 'Serving',
    description: 'Master the art of serving in badminton',
    skills: [
      {
        id: 'basic-serve',
        name: 'Basic Serve',
        description: 'Execute a legal underhand serve',
        difficulty: 'beginner',
        tips: [
          'Use an underhand motion',
          'Contact the shuttle below your waist',
          'Follow through toward your target',
          'Keep your feet stationary during serve'
        ]
      },
      {
        id: 'serve-placement',
        name: 'Serve Placement',
        description: 'Place serves to different areas of the court',
        difficulty: 'beginner',
        prerequisites: ['basic-serve'],
        tips: [
          'Aim for the corners of the service box',
          'Use short and long serves',
          'Mix up placement to keep opponents guessing',
          'Target your opponent\'s weaker side'
        ]
      },
      {
        id: 'flick-serve',
        name: 'Flick Serve',
        description: 'Master the deceptive flick serve',
        difficulty: 'intermediate',
        prerequisites: ['basic-serve', 'serve-placement'],
        tips: [
          'Start with a short serve motion',
          'Flick your wrist at the last moment',
          'Use to catch opponents off guard',
          'Practice consistency and placement'
        ]
      },
      {
        id: 'drive-serve',
        name: 'Drive Serve',
        description: 'Execute a fast, flat drive serve',
        difficulty: 'advanced',
        prerequisites: ['flick-serve'],
        tips: [
          'Use a quick, flat motion',
          'Aim for the back of the service box',
          'Use sparingly to surprise opponents',
          'Focus on accuracy over power'
        ]
      }
    ]
  },
  {
    id: 'strokes',
    name: 'Strokes',
    description: 'Develop consistent stroke technique',
    skills: [
      {
        id: 'clear-basic',
        name: 'Basic Clear',
        description: 'Execute a proper overhead clear',
        difficulty: 'beginner',
        tips: [
          'Use an overhead motion',
          'Contact the shuttle high above your head',
          'Follow through toward your target',
          'Use your whole body for power'
        ]
      },
      {
        id: 'drop-shot',
        name: 'Drop Shot',
        description: 'Execute a controlled drop shot',
        difficulty: 'intermediate',
        prerequisites: ['clear-basic'],
        tips: [
          'Use a similar motion to the clear',
          'Reduce power and add touch',
          'Aim for the front of the court',
          'Focus on placement over power'
        ]
      },
      {
        id: 'smash',
        name: 'Smash',
        description: 'Execute a powerful overhead smash',
        difficulty: 'intermediate',
        prerequisites: ['clear-basic'],
        tips: [
          'Use maximum power and speed',
          'Contact the shuttle at the highest point',
          'Aim for the opponent\'s body or feet',
          'Follow through completely'
        ]
      },
      {
        id: 'net-shot',
        name: 'Net Shot',
        description: 'Execute a delicate net shot',
        difficulty: 'advanced',
        prerequisites: ['drop-shot'],
        tips: [
          'Use a gentle, controlled motion',
          'Aim to just clear the net',
          'Focus on touch and placement',
          'Be ready for the return'
        ]
      }
    ]
  },
  {
    id: 'footwork',
    name: 'Footwork',
    description: 'Master court movement and positioning',
    skills: [
      {
        id: 'basic-footwork',
        name: 'Basic Footwork',
        description: 'Develop fundamental court movement',
        difficulty: 'beginner',
        tips: [
          'Stay on your toes',
          'Use small, quick steps',
          'Always return to center court',
          'Keep your weight forward'
        ]
      },
      {
        id: 'lunge-technique',
        name: 'Lunge Technique',
        description: 'Master the forward lunge',
        difficulty: 'intermediate',
        prerequisites: ['basic-footwork'],
        tips: [
          'Step forward with your racket foot',
          'Keep your knee over your toe',
          'Use your lunge to reach the shuttle',
          'Push back to center court'
        ]
      },
      {
        id: 'court-coverage',
        name: 'Court Coverage',
        description: 'Cover the entire court efficiently',
        difficulty: 'intermediate',
        prerequisites: ['lunge-technique'],
        tips: [
          'Anticipate your opponent\'s shots',
          'Use efficient movement patterns',
          'Stay balanced and ready',
          'Practice court coverage drills'
        ]
      },
      {
        id: 'advanced-footwork',
        name: 'Advanced Footwork',
        description: 'Master complex movement patterns',
        difficulty: 'advanced',
        prerequisites: ['court-coverage'],
        tips: [
          'Use deceptive movement',
          'Master quick direction changes',
          'Develop explosive power',
          'Practice under pressure'
        ]
      }
    ]
  },
  {
    id: 'strategy',
    name: 'Strategy & Tactics',
    description: 'Develop strategic thinking and court awareness',
    skills: [
      {
        id: 'court-positioning',
        name: 'Court Positioning',
        description: 'Understand proper court positioning',
        difficulty: 'beginner',
        tips: [
          'Stay in the center of your side',
          'Move as a team in doubles',
          'Cover your partner\'s weaknesses',
          'Anticipate your opponent\'s shots'
        ]
      },
      {
        id: 'shot-selection',
        name: 'Shot Selection',
        description: 'Choose the right shot for each situation',
        difficulty: 'intermediate',
        prerequisites: ['court-positioning'],
        tips: [
          'Consider your opponent\'s position',
          'Use the right shot for the right situation',
          'Don\'t try to win every point with power',
          'Be patient and wait for opportunities'
        ]
      },
      {
        id: 'doubles-strategy',
        name: 'Doubles Strategy',
        description: 'Develop effective doubles tactics',
        difficulty: 'intermediate',
        prerequisites: ['shot-selection'],
        tips: [
          'Communicate with your partner',
          'Cover the court as a team',
          'Use formation changes strategically',
          'Exploit your opponent\'s weaknesses'
        ]
      },
      {
        id: 'advanced-strategy',
        name: 'Advanced Strategy',
        description: 'Develop sophisticated strategic thinking',
        difficulty: 'advanced',
        prerequisites: ['doubles-strategy'],
        tips: [
          'Study your opponent\'s patterns',
          'Use deception and variety',
          'Control the pace of the game',
          'Master the mental game'
        ]
      }
    ]
  },
  {
    id: 'mental-game',
    name: 'Mental Game',
    description: 'Develop mental toughness and focus',
    skills: [
      {
        id: 'focus-concentration',
        name: 'Focus & Concentration',
        description: 'Maintain focus throughout the match',
        difficulty: 'beginner',
        tips: [
          'Stay in the present moment',
          'Don\'t dwell on mistakes',
          'Focus on the next point',
          'Use breathing techniques to stay calm'
        ]
      },
      {
        id: 'pressure-handling',
        name: 'Pressure Handling',
        description: 'Perform well under pressure',
        difficulty: 'intermediate',
        prerequisites: ['focus-concentration'],
        tips: [
          'Practice pressure situations',
          'Develop pre-shot routines',
          'Stay positive and confident',
          'Use visualization techniques'
        ]
      },
      {
        id: 'match-strategy',
        name: 'Match Strategy',
        description: 'Develop and execute match strategies',
        difficulty: 'advanced',
        prerequisites: ['pressure-handling'],
        tips: [
          'Adapt your strategy during the match',
          'Exploit your opponent\'s weaknesses',
          'Stay patient and disciplined',
          'Make adjustments as needed'
        ]
      }
    ]
  }
];
